import {Collapse, Table, TableCell, TableRow, Tooltip, withStyles} from "@material-ui/core";
import GridBlock from "components/GridBlock";
import WorkableIconExpandButton from "components/button/WorkableIconExpandButton";
import React, {useRef, useState} from "react";
import Typography from "components/Typography";
import {connect} from "react-redux";
import Loading from "components/Loading";
import TableBody from "@material-ui/core/TableBody";
import Tr from "translations/Translations";
import HelpComponent from "components/HelpComponent";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import clsx from "clsx";
import moment from "libs/moment";
import DateFormats from "constants/DateFormats";
import * as PropTypes from "prop-types";
import {canAddApprover, canDeleteApprover} from "./lib/ApprovalAccessUtils";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import WorkableIconAddButton from "components/button/WorkableIconAddButton";
import Confirm from "../../popup/confirm/Confirm";


const style = (theme) => ({
    root: {
        '&>*:nth-child(1)': {
            flexGrow: 1,
        }
    },
    title: {
        marginLeft: theme.spacing(),
        wordBreak: 'break-all',
        cursor: 'pointer'
    },
    fullWidth: {
        width: '100%'
    },
    approvalHistory: {
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(4)
    },
    bold: {
        fontWeight: theme.typography.fontWeightBold
    },
    avoided: {
        color: theme.palette.action.disabled
    },
    waiting: {color: theme.palette.primary.main},
    accepted: {color: theme.palette.common.success},
    rejected: {color: theme.palette.common.error},
    table: {
        '&>tbody>tr:last-child>td': {
            borderBottom: 'none'
        }
    },
    noBorderBottom: {
        borderBottom: 'none'
    },
    approverAlign: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }
})

function ApprovalHistory(
    {
        approvalObjectName,
        approvalHistoryHeader,
        approvalHistory,
        loadApprovalHistory,
        showApproverPopup,
        deleteApprover,
        classes
    }) {

    const [open, setOpen] = useState(false);
    const [addWorking, setAddWorking] = useState(0);
    const [deleteWorking, setDeleteWorking] = useState(0);
    const [deleteApproverOpen, setDeleteApproverOpen] = useState(false);
    const approvalStageAndApprover = useRef([]);

    const openApprovalHistory = () => {
        if (open) {
            setOpen(false);
        } else {
            if (!approvalHistory) {
                loadApprovalHistory(approvalHistoryHeader.approvalId).then(() => {
                    setOpen(true);
                });
            } else {
                setOpen(true);
            }
        }
    }

    const approvalStatus = (status) => {
        if (status === 'ACCEPTED')
            return <Tooltip title={Tr("approval_history_approver_status_accepted")} placement="top"><span><CheckCircleOutlineIcon
                className={classes.accepted}/></span></Tooltip>;
        if (status === 'MASTER_APPROVED')
            return <Tooltip title={Tr("approval_history_approver_status_master_accepted")} placement="top"><span><CheckCircleOutlineIcon
                className={classes.accepted}/></span></Tooltip>;
        else if (status === 'REJECTED')
            return <Tooltip title={Tr("approval_history_approver_status_rejected")} placement="top"><span><ErrorOutlineIcon
                className={classes.rejected}/></span></Tooltip>
        else if (status === 'MASTER_REJECTED')
            return <Tooltip title={Tr("approval_history_approver_status_master_rejected")} placement="top"><span><ErrorOutlineIcon
                className={classes.rejected}/></span></Tooltip>
        else if (status === 'MASTER_STOPPED')
            return <Tooltip title={Tr("approval_history_approver_status_master_stopped")} placement="top"><span><ErrorOutlineIcon
                className={classes.rejected}/></span></Tooltip>
        else if (status === 'AVOIDED')
            return <Tooltip title={Tr("approval_history_approver_status_avoided")} placement="top"><span><HighlightOffIcon className={classes.avoided}/></span></Tooltip>;
        else if (status === 'NOTIFICATION_SENT')
            return <Tooltip title={Tr("approval_history_approver_status_waiting_decision")} placement="top"><span><HelpOutlineIcon className={classes.waiting}/></span></Tooltip>;
        else if (status === 'NOTIFICATION_NOT_SENT_YET')
            return <Tooltip title={Tr("approval_history_approver_status_waiting")} placement="top"><span><HelpOutlineIcon className={classes.avoided}/></span></Tooltip>;
        else
            return <></>
    }

    const doShowApproverPopup = (approval, stage) => {
        setAddWorking(stage.identity);
        showApproverPopup(approval, stage).finally(() => {
            setAddWorking(0);
        })
    }

    const doDeleteApprover = (approval, stage, approver) => {
        setDeleteWorking(approver.identity);
        deleteApprover(approval, stage, approver).then(resp => {
            setDeleteWorking(0);
        });
    }

    return (<>
        <GridBlock
            alignItems='center'
            className={classes.root}
            wrap='nowrap'
        >
            <GridBlock className={classes.root}>
                <Typography
                    variant='body1'
                    className={classes.title}
                    onClick={() => openApprovalHistory()}
                >
                    {approvalHistoryHeader.name}
                </Typography>
                <Typography
                    variant='body1'
                    className={classes.title}
                    onClick={() => openApprovalHistory()}
                >
                    {Tr('approval_history_start_date')}: {moment(approvalHistoryHeader.dateInserted).format(DateFormats.DATETIME_FORMAT)}
                </Typography>
            </GridBlock>

            <GridBlock wrap='nowrap'>
                <WorkableIconExpandButton
                    color='primary'
                    onClick={() => openApprovalHistory()}
                    expanded={open}
                />
            </GridBlock>
        </GridBlock>

        <Collapse in={open}>
            {!approvalHistory && <Loading/>}
            {!!approvalHistory &&
            <GridBlock
                direction='column'
                className={classes.approvalHistory}
            >
                <Table className={classes.table}>
                    <TableBody>
                        {approvalHistory.stages.map((s, index) =>
                            <React.Fragment key={`s${s.identity}`}>
                                <TableRow>
                                    <TableCell rowSpan={(s.approvers.length === 0 ? 1 : s.approvers.length) + 3} className={clsx({[classes.noBorderBottom]:index===approvalHistory.stages.length-1})}>
                                        {s.name}{canAddApprover(approvalHistory, s) &&
                                        <>
                                            <br/>
                                            <WorkableIconAddButton
                                                onClick={() => doShowApproverPopup(approvalHistory, s)}
                                                tooltip={Tr('approval_history_addApprover')}
                                                outlined={true}
                                                color='primary'
                                                working={addWorking === s.identity}
                                                disabled={addWorking > 0}
                                            />
                                        </>}
                                    </TableCell>
                                    <TableCell className={classes.bold}>{Tr('approval_history_sender')}</TableCell>
                                    <TableCell colSpan={3} className={classes.bold}>{Tr('approval_history_sender_senddate')}</TableCell>
                                    <TableCell className={classes.bold}>{Tr('approval_history_comment')}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>{!!s.sender ? `${s.sender.firstName} ${s.sender.lastName} (${s.sender.email})` : '--'}</TableCell>
                                    <TableCell colSpan={3}>{!!s.sender ? s.sender.decisionDate : ''}</TableCell>
                                    <TableCell>{!!s.sender?.comment && <HelpComponent dangerouslySetTextHTML={s.sender.comment}></HelpComponent>}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.bold}>{Tr('approval_history_approver')}</TableCell>
                                    <TableCell className={classes.bold}>{Tr('approval_history_approver_received')}</TableCell>
                                    <TableCell className={classes.bold}>{Tr('approval_history_approver_decision')}</TableCell>
                                    <TableCell className={classes.bold}>{Tr('approval_history_approver_status')}</TableCell>
                                    <TableCell className={classes.bold}>{Tr('approval_history_comment')}</TableCell>
                                </TableRow>
                                {s.approvers.length > 0 && s.approvers.map(a => (
                                    <TableRow key={`a${a.identity}`}>
                                        <TableCell>
                                            <div className={classes.approverAlign}>
                                                <span>{`${a.firstName} ${a.lastName} (${a.email})`}</span>
                                                {canDeleteApprover(approvalHistory, s, a) &&
                                                    <WorkableIconDeleteButton
                                                        onClick={() => {
                                                            approvalStageAndApprover.current = [approvalHistory, s, a];
                                                            setDeleteApproverOpen(true);
                                                        }}
                                                        tooltip={Tr('approval_history_deleteApprover')}
                                                        outlined={true}
                                                        color='primary'
                                                        working={deleteWorking === a.identity}
                                                        disabled={deleteWorking !== 0}
                                                    />
                                                }
                                            </div>
                                        </TableCell>
                                        <TableCell>{a.messageReceiveDate}</TableCell>
                                        <TableCell>{a.decisionDate}</TableCell>
                                        <TableCell>{approvalStatus(a.status)}</TableCell>
                                        <TableCell>{!!a.comment && <HelpComponent dangerouslySetTextHTML={a.comment}></HelpComponent>}</TableCell>
                                    </TableRow>
                                    )
                                )}
                                {s.approvers.length === 0 &&
                                    <TableRow>
                                        <TableCell>{'--'}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                }
                            </React.Fragment>
                        )}
                    </TableBody>
                </Table>
            </GridBlock>
            }
        </Collapse>
        <Confirm
            open={deleteApproverOpen}
            onConfirm={() => {
                setDeleteApproverOpen(false);
                doDeleteApprover(approvalStageAndApprover.current[0], approvalStageAndApprover.current[1], approvalStageAndApprover.current[2]);
                approvalStageAndApprover.current = [];
            }}
            onCancel={() => setDeleteApproverOpen(false)}
            confirmText="approval_history_deleteApprover_confirm"
        />
    </>);
}

ApprovalHistory.propTypes = {
    approvalObjectName: PropTypes.string,
    approvalHistoryHeader: PropTypes.object,
    approvalHistory: PropTypes.object,
    loadApprovalHistory: PropTypes.func,
    showApproverPopup: PropTypes.func,
    deleteApprover: PropTypes.func,
    classes: PropTypes.any,
    autoOpen: PropTypes.bool
}

export default connect((state, props) => ({
    approvalHistory: state.resources?.[`${props.approvalObjectName}ApprovalHistory`]?.[`${props.approvalHistoryHeader.approvalId}`]
}), {})(withStyles(style)(ApprovalHistory))